/**
 * AuthLayout — 鉴权区共用外壳
 *
 * 1:1 移植 design/Novel-admin/Login.html 的双栏布局：
 * - 左侧 AI 主题艺术面板（深色径向渐变 + canvas 粒子星座动画 + 鼠标光晕/视差 + 打字机轮播文案）
 * - 右侧表单面板（白底卡片区，子路由经 <Outlet/> 渲染）
 * 左面板的 canvas 动画 / 场景轮播逐字搬原型 JS，改为 React useEffect + ref 驱动（带 cleanup）。
 * auth 区独有 className 非全局原子类，故在此注入 scoped <style>（逐字搬原型 CSS，tokens 走 var()）。
 */

import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';

/** 左面板艺术文案场景（自动轮播 + 打字机），逐字搬原型 `scenes` */
type Scene = {
  kicker: string;
  code: string;
  label: string;
  big: string;
  poem: string;
  meta: string;
};

const SCENES: Scene[] = [
  {
    kicker: 'Autonomous Intelligence',
    code: 'MMXXVI',
    label: '// THE FUTURE, COMPUTED',
    big: 'The future, computed.',
    poem: '在比特与直觉的交界处，<span class="em">机器开始预见</span>——每一束信号，都在抵达之前被理解。',
    meta: '<span><i></i>NEURAL CORE · ONLINE</span><span>LATENCY 0.8MS</span><span>v2026.05</span>',
  },
  {
    kicker: 'Generative Systems',
    code: 'LATENT-09',
    label: '// LATENT SPACE',
    big: 'Data, learning to dream.',
    poem: '在无数维度的暗流里，<span class="em">模型生长出想象</span>——把噪声，编织成秩序。',
    meta: '<span><i></i>INFERENCE · STREAMING</span><span>14 MODELS</span><span>FP8</span>',
  },
  {
    kicker: 'Predictive Mesh',
    code: 'EU-CENTRAL-1',
    label: '// FORESIGHT ENGINE',
    big: 'Foresight, by design.',
    poem: '每一次决策，都<span class="em">先于未来发生</span>——风险在显形之前，已被听见。',
    meta: '<span><i></i>SIGNALS · 1.2M/S</span><span>HORIZON 72H</span><span>p99</span>',
  },
  {
    kicker: 'Cognitive Fabric',
    code: 'SYNAPSE',
    label: '// SYNAPSE GRID',
    big: 'Intelligence in motion.',
    poem: '数据从不沉睡，<span class="em">思维在网络中流动</span>——昼夜不息，自我演进。',
    meta: '<span><i></i>MESH · 7 NODES</span><span>SYNC 100%</span><span>RT</span>',
  },
  {
    kicker: 'Quantum Horizon',
    code: 'MMXXVI',
    label: '// EVENT STREAM',
    big: 'Where machines begin to see.',
    poem: '在比特的尽头，<span class="em">未来正在被书写</span>——而我们，只是按下开始。',
    meta: '<span><i></i>VISION · ACTIVE</span><span>ENTROPY ↓</span><span>∞</span>',
  },
];

export default function AuthLayout() {
  const panelRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const artRef = useRef<HTMLDivElement>(null);
  const kickerRef = useRef<HTMLSpanElement>(null);
  const codeRef = useRef<HTMLSpanElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const rotRef = useRef<HTMLSpanElement>(null);
  const poemRef = useRef<HTMLParagraphElement>(null);
  const metaRef = useRef<HTMLDivElement>(null);

  // ① canvas 粒子星座 + 鼠标视差 + 数据脉冲（逐字搬原型 tech-mesh IIFE，改 effect + cleanup）
  useEffect(() => {
    const panel = panelRef.current;
    const canvas = canvasRef.current;
    const glow = glowRef.current;
    if (!panel || !canvas || !glow) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    type Node = {
      x: number;
      y: number;
      z: number;
      vx: number;
      vy: number;
      vz: number;
      accent: boolean;
      tw: number;
      tws: number;
      sx: number;
      sy: number;
      r: number;
      depth: number;
    };
    type Star = {
      x: number;
      y: number;
      z: number;
      r: number;
      tw: number;
      tws: number;
      warm: boolean;
    };
    type Pulse = { a: Node; b: Node; t: number; born: number; spd: number };

    let W = 0;
    let H = 0;
    let DPR = 1;
    const resize = () => {
      const r = panel.getBoundingClientRect();
      W = r.width;
      H = r.height;
      DPR = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, W * DPR);
      canvas.height = Math.max(1, H * DPR);
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };
    resize();
    const ro = window.ResizeObserver ? new ResizeObserver(resize) : null;
    ro?.observe(panel);
    window.addEventListener('resize', resize);

    const N = Math.max(120, Math.min(230, Math.round((W * H) / 5200)));
    const nodes: Node[] = [];
    for (let i = 0; i < N; i++) {
      nodes.push({
        x: Math.random(),
        y: Math.random(),
        z: Math.random(),
        vx: (Math.random() - 0.5) * 0.00009,
        vy: (Math.random() - 0.5) * 0.00009,
        vz: (Math.random() - 0.5) * 0.00006,
        accent: Math.random() < 0.22,
        tw: Math.random() * Math.PI * 2,
        tws: 0.6 + Math.random() * 1.8,
        sx: 0,
        sy: 0,
        r: 1,
        depth: 0,
      });
    }

    const SF = Math.max(160, Math.min(420, Math.round((W * H) / 2600)));
    const stars: Star[] = [];
    for (let i = 0; i < SF; i++) {
      stars.push({
        x: Math.random(),
        y: Math.random(),
        z: Math.random() * 0.5,
        r: Math.random() * 1.1 + 0.25,
        tw: Math.random() * Math.PI * 2,
        tws: 0.4 + Math.random() * 1.6,
        warm: Math.random() < 0.18,
      });
    }

    let tmx = 0.5;
    let tmy = 0.5;
    let mxv = 0.5;
    let myv = 0.5;
    let inside = false;
    let gather = 0;
    const onMove = (e: MouseEvent) => {
      const r = panel.getBoundingClientRect();
      tmx = (e.clientX - r.left) / r.width;
      tmy = (e.clientY - r.top) / r.height;
      inside = true;
      glow.style.opacity = '1';
    };
    const onLeave = () => {
      tmx = 0.5;
      tmy = 0.5;
      inside = false;
      glow.style.opacity = '0';
    };
    panel.addEventListener('mousemove', onMove);
    panel.addEventListener('mouseleave', onLeave);
    glow.style.opacity = '0';

    const pulses: Pulse[] = [];
    let lastPulse = 0;
    const spawnPulse = (now: number) => {
      const a = nodes[(Math.random() * N) | 0];
      if (!a) return;
      let best: Node | null = null;
      let bd = 1e9;
      for (const b of nodes) {
        if (b === a) continue;
        const dx = a.sx - b.sx;
        const dy = a.sy - b.sy;
        const d = dx * dx + dy * dy;
        if (d < bd && d > 16) {
          bd = d;
          best = b;
        }
      }
      if (best && bd < 150 * 150)
        pulses.push({ a, b: best, t: 0, born: now, spd: 0.0075 + Math.random() * 0.0075 });
    };
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

    let raf = 0;
    const frame = (now: number) => {
      mxv += (tmx - mxv) * 0.05;
      myv += (tmy - myv) * 0.05;
      glow.style.setProperty('--gx', `${mxv * W}px`);
      glow.style.setProperty('--gy', `${myv * H}px`);
      ctx.clearRect(0, 0, W, H);

      const pX = mxv - 0.5;
      const pY = myv - 0.5;
      const tnow = now * 0.001;

      const _mpx = mxv * W;
      const _mpy = myv * H;
      for (const s of stars) {
        const shift = s.z - 0.5;
        let sx = s.x * W + pX * 60 * shift;
        let sy = s.y * H + pY * 60 * shift;
        if (gather > 0.001) {
          const dx = _mpx - sx;
          const dy = _mpy - sy;
          const dist = Math.hypot(dx, dy) || 1;
          const fall = Math.max(0, 1 - dist / (Math.max(W, H) * 1.1));
          const pull = gather * fall * fall * 0.16;
          sx += dx * pull;
          sy += dy * pull;
        }
        const tw = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(tnow * s.tws + s.tw));
        ctx.fillStyle = s.warm
          ? `rgba(255,200,150,${(tw * 0.5).toFixed(3)})`
          : `rgba(200,214,235,${(tw * 0.55).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(sx, sy, s.r, 0, Math.PI * 2);
        ctx.fill();
      }

      const mpx = mxv * W;
      const mpy = myv * H;
      gather += ((inside ? 1 : 0) - gather) * 0.05;

      for (const n of nodes) {
        if (!reduce) {
          n.x += n.vx;
          n.y += n.vy;
          n.z += n.vz;
        }
        if (n.x < 0) n.x += 1;
        else if (n.x > 1) n.x -= 1;
        if (n.y < 0) n.y += 1;
        else if (n.y > 1) n.y -= 1;
        if (n.z < 0) n.z += 1;
        else if (n.z > 1) n.z -= 1;
        const depth = 0.35 + n.z * 0.65;
        const shift = n.z - 0.5;
        let bx = n.x * W + pX * 150 * shift;
        let by = n.y * H + pY * 150 * shift;
        if (gather > 0.001) {
          const dx = mpx - bx;
          const dy = mpy - by;
          const dist = Math.hypot(dx, dy) || 1;
          const reach = Math.max(W, H) * 0.5;
          const fall = Math.max(0, 1 - dist / reach);
          const pull = gather * fall * fall * (0.16 + depth * 0.28);
          bx += dx * pull;
          by += dy * pull;
        }
        n.sx = bx;
        n.sy = by;
        n.r = depth * 2.3;
        n.depth = depth;
      }

      const MAXD = Math.min(170, Math.max(120, W * 0.16)) * (1 - gather * 0.25);
      const webFade = 1 - gather * 0.2;

      for (let i = 0; i < N; i++) {
        const a = nodes[i];
        if (!a) continue;
        for (let j = i + 1; j < N; j++) {
          const b = nodes[j];
          if (!b) continue;
          const dx = a.sx - b.sx;
          const dy = a.sy - b.sy;
          const d2 = dx * dx + dy * dy;
          if (d2 > MAXD * MAXD) continue;
          const d = Math.sqrt(d2);
          const fade = 1 - d / MAXD;
          const midx = (a.sx + b.sx) * 0.5;
          const midy = (a.sy + b.sy) * 0.5;
          const md = Math.hypot(midx - mpx, midy - mpy);
          const boost = Math.max(0, 1 - md / 240);
          const rr = lerp(150, 245, boost) | 0;
          const gg = lerp(170, 150, boost) | 0;
          const bb = lerp(200, 70, boost) | 0;
          const alpha = fade * (0.07 + boost * 0.5) * (0.55 + a.depth * 0.45) * webFade;
          ctx.strokeStyle = `rgba(${rr},${gg},${bb},${alpha.toFixed(3)})`;
          ctx.lineWidth = boost > 0.4 ? 1.1 : 0.7;
          ctx.beginPath();
          ctx.moveTo(a.sx, a.sy);
          ctx.lineTo(b.sx, b.sy);
          ctx.stroke();
        }
      }

      for (const n of nodes) {
        const md = Math.hypot(n.sx - mpx, n.sy - mpy);
        const near = Math.max(0, 1 - md / 200);
        const twinkle = 0.7 + 0.3 * Math.sin(tnow * n.tws + n.tw);
        if (n.accent || near > 0.3) {
          const rr = 245;
          const gg = lerp(150, 175, near) | 0;
          const bb = lerp(70, 90, near) | 0;
          ctx.fillStyle = `rgba(${rr},${gg},${bb},${((0.5 + near * 0.5) * twinkle).toFixed(3)})`;
          if (near > 0.2) {
            ctx.shadowColor = 'rgba(245,150,70,0.9)';
            ctx.shadowBlur = 8 * near;
          }
        } else {
          ctx.fillStyle = `rgba(200,212,230,${((0.32 + n.depth * 0.4) * twinkle).toFixed(3)})`;
          ctx.shadowBlur = 0;
        }
        ctx.beginPath();
        ctx.arc(n.sx, n.sy, n.r * (1 + near * 0.8), 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      if (!reduce) {
        const interval = inside ? 200 : 420;
        const cap = inside ? 32 : 16;
        if (now - lastPulse > interval && pulses.length < cap) {
          spawnPulse(now);
          lastPulse = now;
        }
        for (let i = pulses.length - 1; i >= 0; i--) {
          const p = pulses[i];
          if (!p) continue;
          p.t += p.spd || 0.01;
          if (p.t >= 1) {
            pulses.splice(i, 1);
            continue;
          }
          const x = lerp(p.a.sx, p.b.sx, p.t);
          const y = lerp(p.a.sy, p.b.sy, p.t);
          const tt = Math.max(0, p.t - 0.42);
          const tx = lerp(p.a.sx, p.b.sx, tt);
          const ty = lerp(p.a.sy, p.b.sy, tt);
          const a = Math.sin(p.t * Math.PI);
          const grad = ctx.createLinearGradient(tx, ty, x, y);
          grad.addColorStop(0, 'rgba(255,170,80,0)');
          grad.addColorStop(0.7, `rgba(255,195,120,${(a * 0.32).toFixed(3)})`);
          grad.addColorStop(1, `rgba(255,214,160,${(a * 0.8).toFixed(3)})`);
          ctx.strokeStyle = grad;
          ctx.lineWidth = 1.4;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(tx, ty);
          ctx.lineTo(x, y);
          ctx.stroke();
          ctx.fillStyle = `rgba(255,228,190,${(a * 0.95).toFixed(3)})`;
          ctx.shadowColor = 'rgba(255,185,95,0.9)';
          ctx.shadowBlur = 8;
          ctx.beginPath();
          ctx.arc(x, y, 1.7, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        }
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener('resize', resize);
      panel.removeEventListener('mousemove', onMove);
      panel.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  // ② 艺术文案场景轮播 + 打字机 + 文字反向视差（逐字搬原型 scenes IIFE，改 effect + cleanup）
  useEffect(() => {
    const art = artRef.current;
    const panel = panelRef.current;
    if (!art) return;
    const elKicker = kickerRef.current;
    const elCode = codeRef.current;
    const elLabel = labelRef.current;
    const elRot = rotRef.current;
    const elPoem = poemRef.current;
    const elMeta = metaRef.current;
    const caret = art.querySelector<HTMLElement>('.art-caret');
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    let alive = true;

    const applyInstant = (s: Scene) => {
      if (elKicker) elKicker.textContent = s.kicker;
      if (elCode) elCode.textContent = s.code;
      if (elLabel) elLabel.textContent = s.label;
      if (elPoem) elPoem.innerHTML = s.poem;
      if (elMeta) elMeta.innerHTML = s.meta;
    };

    let typeTimer: ReturnType<typeof setTimeout> | undefined;
    const typePhrase = (text: string) => {
      clearTimeout(typeTimer);
      if (reduce || !elRot) {
        if (elRot) elRot.textContent = text;
        return;
      }
      caret?.classList.add('typing');
      elRot.textContent = '';
      let k = 0;
      const step = () => {
        if (!alive || !elRot) return;
        elRot.textContent = text.slice(0, ++k);
        if (k < text.length) {
          const ch = text[k - 1];
          const base = 42 + Math.random() * 60;
          const delay = ch === ',' || ch === '.' ? base + 160 : base;
          typeTimer = setTimeout(step, delay);
        } else {
          caret?.classList.remove('typing');
        }
      };
      step();
    };

    applyInstant(SCENES[0] as Scene);
    typePhrase((SCENES[0] as Scene).big);

    let cycle: ReturnType<typeof setInterval> | undefined;
    let swapTimer: ReturnType<typeof setTimeout> | undefined;
    let parallaxRaf = 0;
    let onMove: ((e: MouseEvent) => void) | undefined;
    let onLeave: (() => void) | undefined;

    if (!reduce) {
      let i = 0;
      cycle = setInterval(() => {
        art.classList.add('swapping');
        swapTimer = setTimeout(() => {
          if (!alive) return;
          i = (i + 1) % SCENES.length;
          applyInstant(SCENES[i] as Scene);
          art.classList.remove('swapping');
          typePhrase((SCENES[i] as Scene).big);
        }, 520);
      }, 5600);

      if (panel) {
        let tx = 0;
        let ty = 0;
        let cx = 0;
        let cy = 0;
        onMove = (e: MouseEvent) => {
          const r = panel.getBoundingClientRect();
          tx = (e.clientX - r.left) / r.width - 0.5;
          ty = (e.clientY - r.top) / r.height - 0.5;
        };
        onLeave = () => {
          tx = 0;
          ty = 0;
        };
        panel.addEventListener('mousemove', onMove);
        panel.addEventListener('mouseleave', onLeave);
        const loop = () => {
          cx += (tx - cx) * 0.06;
          cy += (ty - cy) * 0.06;
          art.style.transform = `translate(${(cx * -18).toFixed(2)}px,${(cy * -12).toFixed(2)}px)`;
          parallaxRaf = requestAnimationFrame(loop);
        };
        parallaxRaf = requestAnimationFrame(loop);
      }
    }

    return () => {
      alive = false;
      clearTimeout(typeTimer);
      clearTimeout(swapTimer);
      if (cycle) clearInterval(cycle);
      if (parallaxRaf) cancelAnimationFrame(parallaxRaf);
      if (panel && onMove) panel.removeEventListener('mousemove', onMove);
      if (panel && onLeave) panel.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <div className="auth-root">
      <style>{AUTH_CSS}</style>

      {/* LEFT: AI 艺术品牌面板 */}
      <div className="left-panel" ref={panelRef}>
        <div className="grid-noise" />

        <div className="brand">
          <div className="brand-mark">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 4v16M5 4l14 16M19 4v16"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div>
            <div className="brand-name">Rivmoon Console</div>
            <div className="brand-sub">v2026.05 · EU Operations</div>
          </div>
        </div>

        <canvas className="tech-canvas" ref={canvasRef} />
        <div className="mouse-glow" ref={glowRef} />

        <div className="hero-art" ref={artRef}>
          <div className="art-kicker swap">
            <span className="k-dot" />
            <span ref={kickerRef}>Autonomous Intelligence</span>
            <span className="k-line" />
            <span ref={codeRef}>MMXXVI</span>
          </div>
          <h1 className="art-title swap">
            <span className="art-fixed" ref={labelRef}>
              {'// THE FUTURE, COMPUTED'}
            </span>
            <span className="art-rot" ref={rotRef}>
              The future, computed.
            </span>
            <span className="art-caret" />
          </h1>
          {/* poem / meta 含 <span> 标记，挂载后由 effect 经 innerHTML 接管轮播 */}
          <p className="art-poem swap" ref={poemRef}>
            在比特与直觉的交界处，<span className="em">机器开始预见</span>
            ——每一束信号，都在抵达之前被理解。
          </p>
          <div className="art-meta swap" ref={metaRef}>
            <span>
              <i />
              NEURAL CORE · ONLINE
            </span>
            <span>LATENCY 0.8MS</span>
            <span>v2026.05</span>
          </div>
        </div>
      </div>

      {/* RIGHT: form */}
      <div className="right-panel">
        <Outlet />
        <div className="meta">
          <span>
            需要权限？联系 <a href="mailto:it-support@rivmoon.com">it-support@rivmoon.com</a>
          </span>
          <span className="mono" style={{ fontFamily: "'Geist Mono', monospace" }}>
            build · 2026.05.25
          </span>
        </div>
      </div>
    </div>
  );
}

/** auth 区专属样式（逐字搬 Login.html，tokens 走 var()） */
const AUTH_CSS = `
.auth-root { min-height: 100vh; background: var(--bg); display: flex; }

/* ---------- LEFT BRAND PANEL（AI 艺术面板）---------- */
.auth-root .left-panel {
  flex: 1;
  background:
    radial-gradient(ellipse at 28% 26%, oklch(0.72 0.17 50 / 0.16), transparent 46%),
    radial-gradient(ellipse at 82% 68%, oklch(0.5 0.16 320 / 0.2), transparent 52%),
    radial-gradient(ellipse at 52% 92%, oklch(0.5 0.14 245 / 0.16), transparent 44%),
    radial-gradient(ellipse at 65% 12%, oklch(0.6 0.1 200 / 0.1), transparent 40%),
    radial-gradient(circle at 50% 50%, oklch(0.16 0.012 255), oklch(0.1 0.012 260) 80%);
  color: oklch(0.94 0.005 80);
  padding: 40px 48px;
  display: flex; flex-direction: column; justify-content: space-between;
  position: relative; overflow: hidden; min-width: 0;
}
.auth-root .grid-noise {
  position: absolute; inset: 0;
  background-image:
    linear-gradient(oklch(0.5 0.02 250 / 0.07) 1px, transparent 1px),
    linear-gradient(90deg, oklch(0.5 0.02 250 / 0.07) 1px, transparent 1px);
  background-size: 36px 36px;
  mask-image: linear-gradient(180deg, transparent, black 40%, black 60%, transparent);
  -webkit-mask-image: linear-gradient(180deg, transparent, black 40%, black 60%, transparent);
  pointer-events: none;
}
.auth-root .brand { display: flex; align-items: center; gap: 12px; position: relative; }
.auth-root .brand-mark {
  width: 36px; height: 36px; border-radius: 8px;
  background: linear-gradient(135deg, oklch(0.94 0.005 80), oklch(0.7 0.02 80));
  display: flex; align-items: center; justify-content: center;
  color: oklch(0.155 0.008 250);
}
.auth-root .brand-name { font-family: 'Geist', sans-serif; font-weight: 600; font-size: 18px; letter-spacing: -0.01em; }
.auth-root .brand-sub  { font-family: 'Geist Mono', monospace; font-size: 10.5px; color: oklch(0.7 0.008 80); letter-spacing: 0.04em; text-transform: uppercase; }

/* ---------- TECH MESH ANIMATION ---------- */
.auth-root .tech-canvas {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  pointer-events: none;
  z-index: 0;
}
.auth-root .mouse-glow {
  position: absolute; top: 0; left: 0;
  width: 520px; height: 520px;
  margin: -260px 0 0 -260px;
  border-radius: 999px;
  background: radial-gradient(circle, oklch(0.72 0.19 50 / 0.16), oklch(0.72 0.19 50 / 0.05) 35%, transparent 68%);
  pointer-events: none;
  z-index: 0;
  transform: translate(var(--gx, 50%), var(--gy, 50%));
  transition: opacity 0.4s ease;
  will-change: transform;
}
.auth-root .brand, .auth-root .hero-art { z-index: 1; }

/* ---------- ARTISTIC AI / FUTURE TEXT ---------- */
.auth-root .hero-art { position: relative; max-width: 580px; margin: auto 0; }
.auth-root .art-kicker {
  display: inline-flex; align-items: center; gap: 9px;
  font-family: 'Geist Mono', monospace; font-size: 11px;
  letter-spacing: 0.26em; text-transform: uppercase;
  color: oklch(0.74 0.11 62); margin-bottom: 26px;
}
.auth-root .art-kicker .k-dot {
  width: 6px; height: 6px; border-radius: 999px;
  background: oklch(0.76 0.18 55);
  box-shadow: 0 0 10px oklch(0.76 0.18 55);
  animation: auth-lp 1.8s ease-in-out infinite;
}
.auth-root .art-kicker .k-line { width: 28px; height: 1px; background: linear-gradient(90deg, oklch(0.74 0.11 62), transparent); }
.auth-root .art-title {
  font-family: 'Geist', sans-serif; font-weight: 600;
  font-size: 50px; line-height: 1.05; letter-spacing: -0.03em;
  margin: 0; min-height: 2.1em; color: oklch(0.96 0.005 80);
}
.auth-root .art-title .art-fixed { color: oklch(0.55 0.01 250); display: block; font-size: 19px; font-weight: 500; letter-spacing: 0.02em; margin-bottom: 10px; font-family: 'Geist Mono', monospace; text-transform: uppercase; }
.auth-root .art-rot {
  background: linear-gradient(100deg, oklch(0.95 0.03 75), oklch(0.82 0.16 52) 38%, oklch(0.72 0.19 38) 66%, oklch(0.95 0.04 70));
  background-size: 220% 100%;
  -webkit-background-clip: text; background-clip: text; color: transparent;
  animation: auth-sheen 6.5s linear infinite;
  transition: opacity 0.42s ease, transform 0.42s ease;
  display: inline-block;
}
@keyframes auth-sheen { to { background-position: -220% 0; } }
.auth-root .art-caret {
  display: inline-block; width: 3px; height: 0.92em;
  margin-left: 6px; vertical-align: -0.08em;
  background: oklch(0.76 0.18 55);
  box-shadow: 0 0 8px oklch(0.76 0.18 55 / 0.8);
  animation: auth-caret 1.05s steps(1) infinite;
}
@keyframes auth-caret { 50% { opacity: 0; } }
.auth-root .art-caret.typing { animation: none; opacity: 1; }
.auth-root .art-poem {
  margin-top: 26px; font-size: 15px; line-height: 1.75;
  color: oklch(0.66 0.008 80); max-width: 430px; letter-spacing: 0.01em;
}
.auth-root .art-poem .em { color: oklch(0.82 0.1 60); }
.auth-root .art-meta {
  margin-top: 30px; display: flex; gap: 22px;
  font-family: 'Geist Mono', monospace; font-size: 10.5px;
  letter-spacing: 0.1em; text-transform: uppercase; color: oklch(0.5 0.008 80);
}
.auth-root .art-meta span { display: inline-flex; align-items: center; gap: 6px; }
.auth-root .art-meta i { width: 5px; height: 5px; border-radius: 999px; background: oklch(0.6 0.12 152); font-style: normal; }
.auth-root .hero-art .swap { transition: opacity 0.5s ease, transform 0.5s ease; }
.auth-root .hero-art.swapping .swap { opacity: 0; transform: translateY(12px); }

@keyframes auth-lp { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }

/* ---------- RIGHT FORM PANEL ---------- */
.auth-root .right-panel {
  width: 480px; flex-shrink: 0;
  background: var(--bg-elev);
  border-left: 1px solid var(--border);
  padding: 56px 48px;
  display: flex; flex-direction: column; justify-content: center; gap: 24px;
  position: relative;
}
/* meta 页脚钉在右面板底部（绝对定位），表单仍垂直居中 —— 对齐设计稿更新 */
.auth-root .right-panel > .meta {
  position: absolute; left: 48px; right: 48px; bottom: 24px;
  margin: 0; background: var(--bg-elev);
}

/* Step indicator */
.auth-root .step-indicator {
  position: absolute; top: 32px; right: 48px;
  display: flex; gap: 6px; align-items: center;
  color: var(--fg-muted); font-size: 11px;
  font-family: 'Geist Mono', monospace; letter-spacing: 0.06em; text-transform: uppercase;
}
.auth-root .step-dot { width: 22px; height: 4px; border-radius: 2px; background: var(--border-strong); transition: background 0.2s; }
.auth-root .step-dot.active { background: var(--accent); }
.auth-root .step-dot.done   { background: var(--accent); opacity: 0.45; }

.auth-root .form-title { font-family: 'Geist', sans-serif; font-size: 28px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 6px; color: var(--fg-strong); }
.auth-root .form-sub   { color: var(--fg-muted); font-size: 13px; margin: 0; }

.auth-root .field { display: flex; flex-direction: column; gap: 6px; }
.auth-root .field-label { font-size: 11.5px; font-weight: 500; color: var(--fg-muted); text-transform: uppercase; letter-spacing: 0.04em; }
.auth-root .input-big {
  height: 40px; padding: 0 14px; border-radius: 8px;
  border: 1px solid var(--border); background: var(--bg);
  font-family: inherit; font-size: 14px; color: var(--fg);
  outline: none; transition: border-color 0.12s, box-shadow 0.12s;
  width: 100%; box-sizing: border-box;
}
.auth-root .input-big:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--ring); }

.auth-root .btn-big {
  height: 42px; border-radius: 8px; border: 0;
  background: var(--fg-strong); color: var(--bg);
  font-family: inherit; font-size: 14px; font-weight: 500; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  transition: background 0.12s, transform 0.06s; width: 100%;
}
.auth-root .btn-big:hover { background: var(--fg); }
.auth-root .btn-big:active { transform: scale(0.99); }
.auth-root .btn-big[disabled] { background: var(--bg-muted); color: var(--fg-muted); cursor: not-allowed; }

.auth-root .btn-sso {
  height: 42px; border-radius: 8px; border: 1px solid var(--border); background: var(--bg);
  font-family: inherit; font-size: 13px; color: var(--fg); cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: 10px;
  transition: background 0.12s;
}
.auth-root .btn-sso:hover { background: var(--bg-subtle); }

.auth-root .divider-row { display: flex; align-items: center; gap: 12px; color: var(--fg-faint); font-size: 11px; margin: 4px 0; }
.auth-root .divider-row::before, .auth-root .divider-row::after { content: ""; flex: 1; height: 1px; background: var(--border); }

.auth-root .meta {
  margin-top: 16px; padding-top: 20px; border-top: 1px solid var(--border);
  display: flex; justify-content: space-between; font-size: 11.5px; color: var(--fg-muted);
}
.auth-root .meta a { color: var(--accent); text-decoration: none; }

/* ---------- 2FA STEP ---------- */
.auth-root .otp-row { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 8px; }
.auth-root .otp-input {
  width: 100%; min-width: 0; height: 56px; padding: 0; margin: 0; box-sizing: border-box;
  border-radius: 8px; border: 1.5px solid var(--border); background: var(--bg);
  text-align: center; font-family: 'Geist Mono', monospace; font-size: 24px; font-weight: 600;
  color: var(--fg-strong); outline: none;
  transition: border-color 0.12s, box-shadow 0.12s, background 0.12s;
  appearance: none; -webkit-appearance: none; -moz-appearance: textfield;
}
.auth-root .otp-input::-webkit-outer-spin-button,
.auth-root .otp-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.auth-root .otp-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--ring); background: var(--bg-elev); }
.auth-root .otp-input.filled { border-color: var(--accent); }
.auth-root .otp-input.error  { border-color: var(--neg); box-shadow: 0 0 0 3px oklch(0.58 0.2 25 / 0.18); animation: auth-shake 0.3s ease; }
@keyframes auth-shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-3px); }
  40%, 80% { transform: translateX(3px); }
}

/* ---------- 2FA SETUP / QR ---------- */
.auth-root .setup-grid { display: grid; grid-template-columns: 152px 1fr; gap: 16px; align-items: flex-start; margin-bottom: 18px; }
.auth-root .qr-frame {
  width: 152px; height: 152px; border-radius: 10px; background: white; padding: 8px;
  box-shadow: 0 0 0 1px var(--border), 0 4px 12px oklch(0.18 0.005 80 / 0.06); box-sizing: border-box;
}
.auth-root .qr-frame svg { display: block; width: 100%; height: 100%; image-rendering: pixelated; }
.auth-root .setup-steps { display: flex; flex-direction: column; gap: 10px; }
.auth-root .setup-step { display: flex; gap: 10px; font-size: 12px; line-height: 1.5; }
.auth-root .setup-step-num {
  flex-shrink: 0; width: 20px; height: 20px; border-radius: 999px;
  background: var(--accent-soft); color: var(--accent-fg);
  display: inline-flex; align-items: center; justify-content: center;
  font-family: 'Geist Mono', monospace; font-size: 11px; font-weight: 600;
}
.auth-root .setup-step-title { font-weight: 500; color: var(--fg-strong); font-size: 12.5px; }
.auth-root .setup-step-desc  { color: var(--fg-muted); font-size: 11.5px; margin-top: 1px; }
.auth-root .store-row { display: flex; gap: 6px; margin-top: 6px; }
.auth-root .store-btn {
  height: 24px; padding: 0 8px; border-radius: 5px; border: 1px solid var(--border); background: var(--bg);
  font-family: inherit; font-size: 10.5px; color: var(--fg); text-decoration: none;
  display: inline-flex; align-items: center; gap: 5px; cursor: pointer;
}
.auth-root .store-btn:hover { background: var(--bg-subtle); }
.auth-root .qr-secret-row {
  display: flex; gap: 6px; align-items: center; padding: 8px 10px;
  background: var(--bg-subtle); border-radius: 6px; border: 1px solid var(--border); margin-bottom: 18px;
}
.auth-root .qr-secret { flex: 1; font-family: 'Geist Mono', monospace; font-size: 12px; color: var(--fg); letter-spacing: 0.04em; word-break: break-all; }
.auth-root .qr-secret-copy {
  height: 24px; padding: 0 8px; border-radius: 4px; border: 1px solid var(--border); background: var(--bg-elev);
  font-family: inherit; font-size: 10.5px; color: var(--fg-muted); cursor: pointer;
  display: inline-flex; align-items: center; gap: 4px;
}
.auth-root .qr-secret-copy:hover { color: var(--fg); background: var(--bg-subtle); }
.auth-root .bind-success {
  display: flex; align-items: center; gap: 10px; padding: 10px 12px;
  background: var(--pos-soft); border-left: 2px solid var(--pos); border-radius: 4px;
  font-size: 12px; color: var(--pos); margin-bottom: 12px;
}

.auth-root .twofa-user {
  display: flex; align-items: center; gap: 10px; padding: 10px 12px;
  border: 1px solid var(--border); background: var(--bg-subtle); border-radius: 8px;
}
.auth-root .twofa-avatar {
  width: 32px; height: 32px; border-radius: 999px;
  background: linear-gradient(135deg, oklch(0.6 0.18 35), oklch(0.45 0.14 320));
  color: white; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 13px;
}
.auth-root .twofa-info { flex: 1; min-width: 0; }
.auth-root .twofa-name { font-size: 13px; font-weight: 500; }
.auth-root .twofa-mail { font-family: 'Geist Mono', monospace; font-size: 11px; color: var(--fg-muted); }
.auth-root .twofa-app-icon {
  width: 28px; height: 28px; border-radius: 6px;
  background: linear-gradient(135deg, #4285F4, #34A853);
  color: white; display: inline-flex; align-items: center; justify-content: center;
  font-family: 'Geist', sans-serif; font-size: 13px; font-weight: 700;
}
.auth-root .ga-hint {
  display: flex; align-items: center; gap: 8px; padding: 10px 12px;
  background: var(--info-soft); border-left: 2px solid var(--info); border-radius: 4px;
  font-size: 11.5px; color: var(--fg-muted); line-height: 1.5;
}
.auth-root .ga-hint .strong { color: var(--fg); font-weight: 500; }

.auth-root .err {
  display: flex; align-items: center; gap: 6px; color: var(--neg); font-size: 12px;
  background: var(--neg-soft); padding: 6px 10px; border-radius: 4px;
}

.auth-root .step { animation: auth-stepIn 0.28s ease; }
@keyframes auth-stepIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }

@media (max-width: 960px) {
  .auth-root { flex-direction: column; }
  .auth-root .right-panel { width: 100%; padding: 32px; }
  .auth-root .left-panel { min-height: 360px; padding: 32px; }
  .auth-root .step-indicator { right: 32px; }
}
`;
